/**
 * cypAdhdSsiConfig.js — the frontend half of the CYP ADHD Semi-Structured
 * Interview contract.
 *
 * THE contract is python-functions/constants/cyp_adhd/ssi_data_model.py: the
 * seeder builds the stage document from it, the extractor reads the stored
 * shape with it, and the report renders each generated sentence under the
 * symptom NAME it defines. This file is the JS mirror the proforma page reads.
 *
 * tooltip / suggestedPrompts / homeExamples / educationExamples /
 * examplesHint / examplesFootnote carry the clinician-facing content on each
 * symptom page, authored in cypAdhdSsiContent.js and merged in below. The
 * literals here keep empty defaults so a symptom with no content entry still
 * carries every key, and each element renders only when its content exists.
 *
 * The two are kept honest by a cross-language sync test
 * (python-functions/tests/test_cyp_adhd_ssi_contract_sync.py) that parses this
 * file and asserts every symptom number, name, DSM code, group and description
 * matches, and likewise for the three impairment domains. Editing one side
 * alone fails that test — which matters because a silent divergence here
 * renders a sentence under the wrong heading, and a wrong heading over
 * clinically correct text is the hardest defect to catch in review.
 */

import { CYP_SSI_CONTENT } from './cypAdhdSsiContent';
import { CYP_SSI_DOMAIN_CONTENT } from './cypAdhdSsiDomainContent';

/**
 * The settings evidencing DSM-5 Criterion C, stored per symptom under
 * data.symptoms.{n}.settings. The symptom page offers them as two chips,
 * labelled Home and Education. Ticking a chip writes that setting and
 * mirrors it onto the environment's `met` field, so the report and the AI
 * inputs keep reading one answer. These labels stay "At home" and
 * "At school" because the report sentences use them; the chips do not.
 * "With friends / socially" is not offered; older stage documents may still
 * hold settings.social, which nothing reads.
 */
export const CYP_SSI_SETTINGS = [
  { key: 'home', label: 'At home' },
  { key: 'school', label: 'At school' },
];

/**
 * Home and Education on every symptom page. Each has an example checklist
 * (content field `examplesField`), stored at
 * data.symptoms.{n}.environments.{key}. `settingKey` is the Criterion C
 * setting the chip writes. There is no separate Yes/No for the setting.
 */
export const CYP_SSI_ENVIRONMENTS = [
  { key: 'home', title: 'Home environment', examplesField: 'homeExamples', settingKey: 'home' },
  { key: 'education', title: 'Education environment', examplesField: 'educationExamples', settingKey: 'school' },
];

/**
 * The answer scale for the temporal determinations (six months or more,
 * before 12, after 12) is Yes or No. Clicking the selected button clears
 * it. Inconclusive is no longer offered. A value of 'inconclusive' is still
 * recognised, so a record saved before this change still reads as itself
 * and still never counts as a Yes.
 */
export const CYP_SSI_INCONCLUSIVE = 'inconclusive';

/** The display label for a stored determination, or null when unanswered. */
export function cypSsiDeterminationLabel(value) {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  if (value === CYP_SSI_INCONCLUSIVE) return 'Inconclusive';
  return null;
}

/**
 * The short label the adult ADHD symptom list shows for this item.
 * Inattention is "A 1" to "A 9"; hyperactivity and impulsivity is
 * "H/I 1" to "H/I 9". The full symptom wording is the hover text, not
 * a second line in the list.
 */
export function cypSsiSymptomNavPrefix(symptom) {
  const place = symptom?.group === 'inattention'
    ? symptom.number
    : (symptom?.number || 0) - 9;
  return symptom?.group === 'inattention' ? `A ${place}` : `H/I ${place}`;
}

/** True once a determination holds any of the three answers. */
export function isCypSsiDetermined(value) {
  return cypSsiDeterminationLabel(value) !== null;
}

/**
 * Kept so a reader of an older interview can see which age the Current /
 * Primary school ticks used to apply to. The symptom panel no longer shows
 * those ticks; any periods already stored stay on the stage document.
 */
export const EXAMPLE_PERIODS_MIN_AGE = 13;

/**
 * The 18 DSM-5 ADHD symptoms in DSM order: 1-9 inattention (Criterion A1),
 * 10-18 hyperactivity and impulsivity (Criterion A2).
 */
export const CYP_SSI_SYMPTOMS = [
  { number: 1, group: 'inattention', dsmCode: 'A1a', name: 'Attention to Detail', description: 'Often fails to give close attention to details or makes careless mistakes in schoolwork or during other activities', tooltip: '', suggestedPrompts: [], homeExamples: [], educationExamples: [], examplesHint: '', examplesFootnote: '' },
  { number: 2, group: 'inattention', dsmCode: 'A1b', name: 'Keeping Attention', description: 'Often has difficulty keeping attention on tasks or play activities', tooltip: '', suggestedPrompts: [], homeExamples: [], educationExamples: [], examplesHint: '', examplesFootnote: '' },
  { number: 3, group: 'inattention', dsmCode: 'A1c', name: 'Listening', description: 'Often does not seem to listen when spoken to directly', tooltip: '', suggestedPrompts: [], homeExamples: [], educationExamples: [], examplesHint: '', examplesFootnote: '' },
  { number: 4, group: 'inattention', dsmCode: 'A1d', name: 'Following Instructions', description: 'Often does not follow through on instructions and fails to finish schoolwork or chores', tooltip: '', suggestedPrompts: [], homeExamples: [], educationExamples: [], examplesHint: '', examplesFootnote: '' },
  { number: 5, group: 'inattention', dsmCode: 'A1e', name: 'Organisation', description: 'Often has difficulty organising tasks and activities', tooltip: '', suggestedPrompts: [], homeExamples: [], educationExamples: [], examplesHint: '', examplesFootnote: '' },
  { number: 6, group: 'inattention', dsmCode: 'A1f', name: 'Engagement in Tasks', description: 'Often avoids, dislikes, or is reluctant to do tasks that need sustained mental effort (such as schoolwork or homework)', tooltip: '', suggestedPrompts: [], homeExamples: [], educationExamples: [], examplesHint: '', examplesFootnote: '' },
  { number: 7, group: 'inattention', dsmCode: 'A1g', name: 'Losing Items', description: 'Often loses things needed for tasks or activities (such as school materials, pencils, books, or keys)', tooltip: '', suggestedPrompts: [], homeExamples: [], educationExamples: [], examplesHint: '', examplesFootnote: '' },
  { number: 8, group: 'inattention', dsmCode: 'A1h', name: 'Distractions', description: 'Is often easily distracted by things going on around them', tooltip: '', suggestedPrompts: [], homeExamples: [], educationExamples: [], examplesHint: '', examplesFootnote: '' },
  { number: 9, group: 'inattention', dsmCode: 'A1i', name: 'Forgetfulness', description: 'Is often forgetful in daily activities (such as doing chores or bringing things to school)', tooltip: '', suggestedPrompts: [], homeExamples: [], educationExamples: [], examplesHint: '', examplesFootnote: '' },
  { number: 10, group: 'hyperactivityImpulsivity', dsmCode: 'A2a', name: 'Fidgeting', description: 'Often fidgets with or taps hands or feet, or squirms in their seat', tooltip: '', suggestedPrompts: [], homeExamples: [], educationExamples: [], examplesHint: '', examplesFootnote: '' },
  { number: 11, group: 'hyperactivityImpulsivity', dsmCode: 'A2b', name: 'Remaining Seated', description: 'Often leaves their seat when staying seated is expected (such as in the classroom)', tooltip: '', suggestedPrompts: [], homeExamples: [], educationExamples: [], examplesHint: '', examplesFootnote: '' },
  { number: 12, group: 'hyperactivityImpulsivity', dsmCode: 'A2c', name: 'Restlessness', description: 'Often runs about or climbs in situations where it is not appropriate (in older children this may be feeling very restless)', tooltip: '', suggestedPrompts: [], homeExamples: [], educationExamples: [], examplesHint: '', examplesFootnote: '' },
  { number: 13, group: 'hyperactivityImpulsivity', dsmCode: 'A2d', name: 'Engaging in Quiet Activities', description: 'Is often unable to play or take part in leisure activities quietly', tooltip: '', suggestedPrompts: [], homeExamples: [], educationExamples: [], examplesHint: '', examplesFootnote: '' },
  { number: 14, group: 'hyperactivityImpulsivity', dsmCode: 'A2e', name: 'Always “On the Go”', description: 'Is often “on the go”, acting as if “driven by a motor”', tooltip: '', suggestedPrompts: [], homeExamples: [], educationExamples: [], examplesHint: '', examplesFootnote: '' },
  { number: 15, group: 'hyperactivityImpulsivity', dsmCode: 'A2f', name: 'Talking A Lot', description: 'Often talks excessively', tooltip: '', suggestedPrompts: [], homeExamples: [], educationExamples: [], examplesHint: '', examplesFootnote: '' },
  { number: 16, group: 'hyperactivityImpulsivity', dsmCode: 'A2g', name: 'Talking Without Thinking', description: 'Often blurts out an answer before a question has been completed', tooltip: '', suggestedPrompts: [], homeExamples: [], educationExamples: [], examplesHint: '', examplesFootnote: '' },
  { number: 17, group: 'hyperactivityImpulsivity', dsmCode: 'A2h', name: 'Difficulty Waiting', description: 'Often has difficulty waiting their turn', tooltip: '', suggestedPrompts: [], homeExamples: [], educationExamples: [], examplesHint: '', examplesFootnote: '' },
  { number: 18, group: 'hyperactivityImpulsivity', dsmCode: 'A2i', name: 'Interrupting Others', description: 'Often interrupts or intrudes on others (such as butting into conversations or games)', tooltip: '', suggestedPrompts: [], homeExamples: [], educationExamples: [], examplesHint: '', examplesFootnote: '' },
].map((symptom) => ({
  // Clinician-facing content is authored once in cypAdhdSsiContent.js and
  // merged in by DSM code, rather than inlined above. Keeping ~200 example
  // strings out of these one-line literals is what makes both files readable,
  // and keying by dsmCode means content cannot be attached to the wrong
  // symptom by an ordering mistake.
  //
  // Spread order matters: the content object wins over the empty defaults
  // above, and a symptom with no entry keeps its empty arrays, which every
  // consumer already treats as "render nothing".
  ...symptom,
  ...(CYP_SSI_CONTENT[symptom.dsmCode] || {}),
}));

/**
 * Older interviews stored one overall level here
 * (data.functionalImpairmentLevel). The domain pages no longer ask it: the
 * Yes/No on each domain is "at least moderate impairment", which is the
 * threshold Adult ADHD uses. These labels stay so a stored value can still
 * be recognised.
 */
export const CYP_SSI_IMPAIRMENT_LEVELS = ['Moderate', 'Severe', 'Not applicable'];

/**
 * The three functional-impairment domains (DSM-5 Criterion D), in report
 * order. Three, not four: "Impact on the Family" was removed from the report
 * and the SSI doc's Personal / Educational / Social map onto these (decision,
 * Sai after speaking with Jasmine, 26 Aug). See SSI_IMPAIRMENT_DOMAINS in
 * python-functions/constants/cyp_adhd/ssi_data_model.py for the full note.
 *
 * outputKey is the reportContent key the backend writes each summary to. It is
 * spelled out rather than derived (`cyp_ssi_${key}ImpairmentSummary`) so the
 * review page cannot quietly build a key the pipeline never produces: a
 * derived key that misses renders an empty review section, whereas a literal
 * one is covered by the cross-language sync test.
 */
export const CYP_SSI_IMPAIRMENT_DOMAINS = [
  { key: 'education', title: 'Education and Learning', outputKey: 'cyp_ssi_educationImpairmentSummary' },
  { key: 'social', title: 'Relationships and Social Interactions', outputKey: 'cyp_ssi_socialImpairmentSummary' },
  { key: 'psychological', title: 'Emotional Wellbeing and Feelings', outputKey: 'cyp_ssi_psychologicalImpairmentSummary' },
].map((domain) => ({
  // Clinician-facing content (tooltip, prompts, example checklists) is
  // authored in cypAdhdSsiDomainContent.js and merged in by key — the same
  // pattern CYP_SSI_SYMPTOMS uses above, and for the same reason: the
  // literals here stay a readable three-line contract (which the Python sync
  // test parses), and content cannot attach to the wrong domain by ordering.
  ...domain,
  ...(CYP_SSI_DOMAIN_CONTENT[domain.key] || {}),
}));

/** The reportContent key holding one symptom's generated sentence. */
export function symptomOutputKey(number) {
  return `cyp_ssi_symptom_${number}_sentence`;
}

/**
 * Stable id for an emerging differential, derived from its ICD-11 label.
 *
 * The Diagnostic Decision keys its per-condition outcomes by this id, so it
 * must be stable across saves — a regenerated id would orphan a recorded
 * clinical decision. Deriving it from the label rather than from a counter or
 * a timestamp is what makes it stable; the DD's extractor also appends any
 * outcome whose id has left the list rather than dropping it, so even a label
 * correction cannot silently discard a decision.
 */
export function differentialId(label) {
  return String(label || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// The onset question is asked from this age up, unless the org's stamp asks
// the before-12 / after-12 pair at every age (TOC). A child younger than this
// is still inside the age-12 window, so persistence already answers onset.
export const CYP_ONSET_QUESTION_MIN_AGE = 13;

// In onset-pair mode, "present after turned 12" is asked from this age up. A
// younger child has no after-12 period yet, so that column is not shown.
export const CYP_AFTER_TWELVE_MIN_AGE = 12;

/**
 * Age in whole years, or null when the date of birth is missing or unreadable.
 * The SSI page, the DD bridge and the MDT Review all need the same answer,
 * because it decides which onset columns a summary is allowed to show.
 */
export function ageFromDateOfBirth(dateOfBirth, now = new Date()) {
  if (!dateOfBirth) return null;
  const dob = new Date(dateOfBirth);
  if (Number.isNaN(dob.getTime())) return null;
  let age = now.getFullYear() - dob.getFullYear();
  const monthDiff = now.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < dob.getDate())) age -= 1;
  return age;
}

/**
 * Which onset columns the symptom summary shows.
 *
 * Review & Synthesis and the MDT Review both call this, so a child is never
 * shown a column for a question the interview did not ask. Unknown age fails
 * open: collect and show the answer rather than hiding a decision that was
 * recorded.
 */
export function cypSsiSummaryColumns({ dateOfBirth, useOnsetPair = false, now } = {}) {
  const age = ageFromDateOfBirth(dateOfBirth, now);
  return {
    onsetAsked: Boolean(useOnsetPair)
      || typeof age !== 'number'
      || age >= CYP_ONSET_QUESTION_MIN_AGE,
    afterTwelveAsked: Boolean(useOnsetPair)
      && (typeof age !== 'number' || age >= CYP_AFTER_TWELVE_MIN_AGE),
  };
}

/**
 * Build the decision-table rows from a stored SSI stage's data.
 *
 * Shared by the SSI's Review & Synthesis step and the MDT Review —
 * both show the same tables. One builder means the two surfaces cannot
 * disagree about a determination the clinician already recorded.
 */
export function buildCypSsiDecisionRows(ssiData) {
  const symptomData = ssiData?.symptoms || {};
  const impairmentData = ssiData?.functionalImpairment || {};
  const toRow = (symptom) => {
    const stored = symptomData[String(symptom.number)] || {};
    const settings = stored.settings || {};
    return {
      dsmCode: symptom.dsmCode,
      name: symptom.name,
      persisted: stored.persistedSixMonths ?? null,
      beforeTwelve: stored.presentBeforeTwelve ?? null,
      // Onset-pair capture (TOC, TDI-205); null for patients whose org asks
      // before-12 only, and for a child who has not yet turned 12.
      afterTwelve: stored.presentAfterTwelve ?? null,
      settingsCount: CYP_SSI_SETTINGS.filter((s) => settings[s.key] === true).length,
    };
  };
  return {
    inattention: CYP_SSI_SYMPTOMS
      .filter((s) => s.group === 'inattention').map(toRow),
    hyperactivity: CYP_SSI_SYMPTOMS
      .filter((s) => s.group === 'hyperactivityImpulsivity').map(toRow),
    impairmentLevel: ssiData?.functionalImpairmentLevel ?? null,
    domains: CYP_SSI_IMPAIRMENT_DOMAINS.map((domain) => ({
      key: domain.key,
      title: domain.title,
      impaired: (impairmentData[domain.key] || {}).isImpairedSelection ?? null,
    })),
  };
}

/**
 * The DD's DSM criteria values, derived mathematically from the recorded SSI
 * determinations — one entry per criterion with the value the counts imply
 * and a summary line saying which counts implied it.
 *
 * The DD page PRE-FILLS the criteria pills from these (Jasmine's CYP criteria
 * doc + standup, 27 Aug: "these yes/nos should get pre-selected; the only one
 * that is not is Criterion E"). The derivation must stay purely mathematical
 * over the clinician's own SSI decisions — the platform never adds clinical
 * judgement of its own, it only counts what the clinician recorded (the
 * medical-device boundary Sai called out). The clinician can override any
 * pre-filled value; Criterion E is never derived or pre-filled.
 *
 * Thresholds (Jasmine's CYP criteria doc, 27 Aug):
 *   A: six or more symptoms per group. One threshold for ALL CYP for UAT —
 *      the future carve-out for 17+ (adult DSM criteria) is out of scope.
 *   B and C: "several" = three or more, as on the adult page.
 *   For a child of 12 or under the onset question is not asked — being under
 *   13 IS being inside the age-12 window — so B is evidenced by the persisted
 *   symptoms themselves (>=3 persisted implies >=3 present before 12), and
 *   only alongside them: three persisted symptoms give B met with A not met.
 */
export const CYP_DSM_CRITERION_A_THRESHOLD = 6;

export function buildCypDsmCriterionEvidence(ssiData, {
  asksOnsetQuestion = true,
} = {}) {
  const rows = buildCypSsiDecisionRows(ssiData);
  const symptomData = ssiData?.symptoms || {};
  const aThreshold = CYP_DSM_CRITERION_A_THRESHOLD;

  const persistedIn = (groupRows) => groupRows.filter((r) => r.persisted === true).length;
  const inattentionCount = persistedIn(rows.inattention);
  const hyperactivityCount = persistedIn(rows.hyperactivity);

  const persistedTotal = inattentionCount + hyperactivityCount;
  const beforeTwelveCount = [...rows.inattention, ...rows.hyperactivity]
    .filter((r) => r.beforeTwelve === true).length;

  // Criterion C: several (>=3) persisted symptoms in each of >=2 settings.
  const settingCounts = {};
  for (const symptom of CYP_SSI_SYMPTOMS) {
    const stored = symptomData[String(symptom.number)] || {};
    if (stored.persistedSixMonths !== true) continue;
    for (const setting of CYP_SSI_SETTINGS) {
      if ((stored.settings || {})[setting.key] === true) {
        settingCounts[setting.key] = (settingCounts[setting.key] || 0) + 1;
      }
    }
  }
  const settingsMeetingThreshold = Object.values(settingCounts)
    .filter((count) => count >= 3).length;

  const impairedCount = rows.domains.filter((d) => d.impaired === true).length;

  // Keyed by the DD page's CYP_DSM_CRITERIA keys, so the render is a lookup.
  return {
    inattentionThreshold: {
      suggestsMet: inattentionCount >= aThreshold,
      summary: `SSI recorded ${inattentionCount} of 9 inattention symptoms `
        + `persisted (threshold ${aThreshold}).`,
    },
    hyperactivityThreshold: {
      suggestsMet: hyperactivityCount >= aThreshold,
      summary: `SSI recorded ${hyperactivityCount} of 9 hyperactivity/`
        + `impulsivity symptoms persisted (threshold ${aThreshold}).`,
    },
    onsetBeforeTwelve: asksOnsetQuestion
      ? {
        suggestsMet: beforeTwelveCount >= 3,
        summary: `SSI recorded ${beforeTwelveCount} symptoms present before age 12.`,
      }
      : {
        suggestsMet: persistedTotal >= 3,
        summary: `Onset is implied at this age: ${persistedTotal} persisted `
          + 'symptoms, all inside the age-12 window.',
      },
    acrossSettings: {
      suggestsMet: settingsMeetingThreshold >= 2,
      summary: 'SSI recorded three or more persisted symptoms in '
        + `${settingsMeetingThreshold} of ${CYP_SSI_SETTINGS.length} settings.`,
    },
    functionalImpairment: {
      suggestsMet: impairedCount >= 1,
      summary: `SSI recorded impairment in ${impairedCount} of `
        + `${rows.domains.length} areas of life.`,
    },
    // Criterion E is never suggested: exclusion of other explanations is
    // clinical judgement with no SSI counterpart, exactly as on the adult
    // page ("Criterion E is not pre-selected").
    notBetterExplained: null,
  };
}

/**
 * True when the SSI has at least one recorded symptom determination.
 *
 * The DD's pre-fill gate: an untouched SSI would derive "not met" for every
 * criterion from counts of zero, stamping clinical answers computed from no
 * clinical decisions at all. Pre-fill only ever runs on top of real
 * determinations.
 */
export function cypSsiHasDeterminations(ssiData) {
  return Object.values(ssiData?.symptoms || {}).some(
    (stored) => stored?.persistedSixMonths === true || stored?.persistedSixMonths === false,
  );
}
